<template>
  <div>
    <div v-if="!showExportSelector">
      <div class="content-box">
        <div v-if="loading && !errors" class="loading-container">
          <div class="loading-main">Loading...</div>
          <div class="loading-sub">Please wait, while we get things ready</div>
          <div class="dots">
            <div class="dot" v-for="n in 5" :key="n" :style="{ animationDelay: `${n * 0.2}s` }"></div>
          </div>
        </div>
        
        <div v-else-if="errors" class="error-container">
          <div class="error-message">{{ errorMessage }}</div>
          <TextButton
            buttonText="Go Back"
            :dark="true"
            @click="goBackFromError"
            class="go-back-button"
          />
        </div>
        
        <div v-else class="prompt">
          <!-- Initial Selection: Select Participant or Export Data -->
          <div v-if="currentStep === 'initialSelection'">
            <div class="heading">ACT Interviewer App Home Page</div>
            <p class="question">What would you like to do?</p>
            <div class="button-container">
              <TextButton
                buttonText="Select a Participant"
                :dark="true"
                @click="currentStep = 'subjectSelection'"
              />
              <TextButton
                buttonText="Export Data"
                :dark="true"
                @click="showExportSelector = true"
              />
            </div>
          </div>

          <!-- Step 1: Subject ID Selection -->
          <div v-if="currentStep === 'subjectSelection'">
            <TextButton
              buttonText="Back"
              :dark="true"
              @click="currentStep = 'initialSelection'"
              class="back-button"
            />
            <div class="heading">ACT Interviewer App</div>
            <p class="question">Please select a Subject ID</p>
            <div class="input-container">
              <label for="subjectIDSelect" class="subject-label">Select Subject ID:</label>
              <select 
                id="subjectIDSelect"
                v-model="selectedSubjectIDOption"
                class="subject-select"
                @change="handleSubjectIDSelection"
              >
                <option value="">Select an ID...</option>
                <option v-for="id in testSubjectIDs" :key="id" :value="id">
                  {{ id === 'custom' ? 'Custom Subject ID' : id }}
                </option>
              </select>

              <div v-if="showCustomInput" class="custom-input-container">
                <label for="customSubjectID" class="subject-label">Enter Custom Subject ID:</label>
                <input 
                  id="customSubjectID"
                  v-model="subjectID" 
                  type="text" 
                  placeholder="Enter Custom Subject ID" 
                  class="subject-input"
                />
              </div>
              <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
              
              <TextButton
                buttonText="Next"
                :dark="true"
                @click="validateAndProceedToVisitSelection"
                class="next-button"
              />
            </div>
          </div>

          <!-- Step 2: Visit Type Selection -->
          <div v-if="currentStep === 'visitSelection'">
            <TextButton
              buttonText="Back"
              :dark="true"
              @click="goBackToSubjectSelection"
              class="back-button"
            />
            <div class="heading">ACT Interviewer App</div>
            <p class="question">Select Visit Type</p>
            <div class="input-container">
              <label for="visitTypeSelect" class="subject-label">Visit Type:</label>
              <select 
                id="visitTypeSelect"
                v-model="visitType"
                class="subject-select"
              >
                <option value="">Select a visit type...</option>
                <option value="Biennial 2025">Biennial 2025</option>
              </select>
              <p v-if="visitTypeError" class="error-message">{{ visitTypeError }}</p>
              
              <TextButton
                buttonText="Next"
                :dark="true"
                @click="validateAndProceedToActivity"
                class="next-button"
              />
            </div>
          </div>

          <!-- Step 3: Activity Selection -->
          <div v-if="currentStep === 'activitySelection'">
            <TextButton
              buttonText="Back"
              :dark="true"
              @click="goBackToVisitSelection"
              class="back-button"
            />
            <div class="heading">ACT Interviewer App</div>
            <p class="question">Ready to begin the assessment workflow?</p>
            <div class="button-container">
              <TextButton
                buttonText="Administering or Scoring"
                :dark="true"
                @click="startSession"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <ExportSubjectSelector
      v-else
      @close="showExportSelector = false"
      @export="handleExport"
    />
  </div>
</template>

<script>
import TextButton from '../components/TextButton.vue';
import ServerCodeModel from '../models/ServerCodeModel.js'
import ExportSubjectSelector from '../components/ExportSubjectSelector.vue'
import { exportSelectedSubjects } from '../utils/export'
import ACTApiService from '../services/ACTApiService.js';

export default {
  components: {
    TextButton,
    ExportSubjectSelector
  },
  data() {
    return {
      currentStep: 'initialSelection',
      userSelection: null,
      subjectID: '',
      visitType: '',
      errorMessage: '',
      visitTypeError: '',
      serverCodeModel: new ServerCodeModel(),
      loading: false,
      errors: false,
      ls: localStorage,
      testSubjectIDs: [],
      selectedSubjectIDOption: '',
      showCustomInput: false,
      showExportSelector: false,
      actApiService: new ACTApiService(),
      participants: [],
      participantMap: {}
    };
  },
  async created() {
    this.testSubjectIDs.push('custom');
    
    try {
      await this.actApiService.getTokenFromCognito();
      const actGroupId = await this.actApiService.getLGroups();
      console.log('ACT Group ID:', actGroupId);
      
      if (actGroupId) {
        const visitTypeId = await this.actApiService.getVisitTypes(actGroupId);
        console.log('Biennial Visit Type ID:', visitTypeId);
        
        this.participants = await this.actApiService.getParticipants(actGroupId);        
        this.participantMap = {};
        
        const displayIds = [];
        this.participants.forEach(participant => {
          this.participantMap[participant.ParticipantDisplayId] = participant;
          displayIds.push(participant.ParticipantDisplayId);
        });
        
        displayIds.sort((a, b) => a.localeCompare(b));
        this.testSubjectIDs = ['custom', ...displayIds];
      } else {
        this.errors = true;
        this.errorMessage = "Failed to retrieve ACT group ID";
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      this.errors = true;
      this.errorMessage = "Failed to load participant data. Please check your connection and try again.";
    }
  },
  methods: {
    handleSubjectIDSelection() {
      if (this.selectedSubjectIDOption === 'custom') {
        this.showCustomInput = true;
        this.subjectID = '';
      } else {
        this.showCustomInput = false;
        const participant = this.participantMap[this.selectedSubjectIDOption];
        if (participant) {
          // Clear previous log IDs before setting new participant
          this.clearLogIds();
          
          this.subjectID = this.selectedSubjectIDOption;
          localStorage.setItem('subjectId', participant.ParticipantDisplayId);
          localStorage.setItem('currentParticipantId', participant.ParticipantId);
        } else {
          this.subjectID = this.selectedSubjectIDOption;
        }
      }
      this.errorMessage = '';
    },
    
    validateAndProceedToVisitSelection() {
      if (this.selectedSubjectIDOption === '') {
        this.errorMessage = "Please select a Subject ID";
        return;
      }
      
      if (this.selectedSubjectIDOption === 'custom') {
        if (!this.subjectID.trim() || !/^[a-zA-Z0-9]+$/.test(this.subjectID)) {
          this.errorMessage = "Please enter a valid Subject ID";
          return;
        }
        
        // Clear previous log IDs before setting new custom participant
        this.clearLogIds();
        
        localStorage.setItem('subjectId', this.subjectID);
        localStorage.setItem('currentParticipantId', this.subjectID);
      }
      
      this.currentStep = 'visitSelection';
    },
    
    validateAndProceedToActivity() {
      if (!this.visitType) {
        this.visitTypeError = "Please select a visit type";
        return;
      }
      
      localStorage.setItem('selectedVisitType', this.visitType);
      
      if (this.visitType === 'Biennial 2025') {
        const visitTypeId = localStorage.getItem('biennialVisitTypeId');
        if (visitTypeId) {
          localStorage.setItem('visitTypeId', visitTypeId);
        }
      }
      
      this.currentStep = 'activitySelection';
    },
    
    goBackToSubjectSelection() {
      this.currentStep = 'subjectSelection';
      this.visitType = '';
      this.visitTypeError = '';
    },
    
    goBackToVisitSelection() {
      this.currentStep = 'visitSelection';
      this.visitTypeError = '';
    },
    
    async startSession() {
      this.userSelection = 'administer';
      this.$emit('selection-changed', this.userSelection);
      console.log("User started session:", this.userSelection);
      
      const participantId = localStorage.getItem('currentParticipantId');
      const visitTypeId = localStorage.getItem('visitTypeId');
      const lGroupId = localStorage.getItem('actGroupId');
      const serverCode = localStorage.getItem('subjectId');
      
      if (!participantId || !visitTypeId) {
        console.error('Missing required IDs for visit API calls');
        return;
      }
      
      this.loading = true;
      
      try {
        const visitResult = await this.actApiService.getVisit(participantId, visitTypeId);
        
        if (visitResult.success) {
          console.log('Visit exists:', visitResult.data);
          localStorage.setItem('visitId', visitResult.data.VisitId);
          
          try {
            const logs = await this.actApiService.getVisitLogs(visitResult.data.VisitId);
            if (logs.length === 0) {
              console.log('No logs associated with this visit yet');
            } else {
              console.log('Visit logs retrieved for existing visit:', logs);
              
              this.processAndStoreLogIds(logs);
            }
          } catch (logsError) {
            console.error('Error fetching logs for existing visit:', logsError);
          }
        } else if (visitResult.notFound) {
          console.log('Visit not found, creating a new one');
          
          try {
            const createResult = await this.actApiService.createVisit(
              lGroupId, 
              participantId, 
              visitTypeId, 
              serverCode
            );
            
            if (createResult && createResult.success) {
              console.log('Visit creation successful (despite possible error status)');
            } else {
              console.log('Visit creation request sent');
            }
          } catch (createError) {
            console.error('Error in visit creation request:', createError);
          }
          
          console.log('Attempting to fetch visit after creation attempt');
          const refetchResult = await this.actApiService.getVisit(participantId, visitTypeId);
          if (refetchResult.success) {
            console.log('Visit fetched after creation:', refetchResult.data);
            localStorage.setItem('visitId', refetchResult.data.VisitId);
            
            try {
              const logs = await this.actApiService.getVisitLogs(refetchResult.data.VisitId);
              if (logs.length === 0) {
                console.log('No logs associated with this new visit yet');
              } else {
                console.log('Visit logs retrieved for new visit:', logs);
                
                this.processAndStoreLogIds(logs);
              }
            } catch (logsError) {
              console.error('Error fetching logs for new visit:', logsError);
            }
          } else {
            console.error('Failed to fetch visit after creation attempt:', refetchResult.error);
          }
        } else {
          console.error('Error checking for visit:', visitResult.error);
        }
      } catch (error) {
        console.error('Error in visit API flow:', error);
      } finally {
        this.loading = false;
        this.startProcess();
      }
    },
    
    resetSelection() {
      this.currentStep = 'initialSelection';
      this.userSelection = null;
      this.visitType = '';
      this.subjectID = '';
      this.selectedSubjectIDOption = '';
      this.showCustomInput = false;
      this.errorMessage = '';
      this.visitTypeError = '';
      // Clear stored IDs when resetting selection
      localStorage.removeItem('subjectId');
      localStorage.removeItem('currentParticipantId');
      localStorage.removeItem('visitTypeId');
      localStorage.removeItem('selectedVisitType');
      localStorage.removeItem('visitId');
      
      this.clearLogIds();
      
      console.log("User reset selection");
    },
    
    startProcess() {
      this.serverCodeModel.clearData();
      // Use the display ID for processing
      const subjectId = localStorage.getItem('subjectId') || this.subjectID;
      console.log(`Using subject ID: ${subjectId} for processing`);
      
      this.startAdminister(subjectId); 
    },

    async startAdminister(subjectId) {
      this.loading = true;
      await this.serverCodeModel.loadForAdminister(subjectId);
      if (this.serverCodeModel.loadForAdministerResult.ok) {
        // yay
        this.gotoFirstTask();
        return;
      } 
      
      // aww
      this.errors = true;
      this.errorMessage = "Darn, it didn't work";
      this.loading = false;
    },

    gotoFirstTask() {
      this.$router.push({
          path: '/introduction',
          query: { selection: this.userSelection }
        });
    },

    goBackFromError() {
      this.loading = false;
      this.errors = false;
      this.errorMessage = '';
      this.currentStep = 'initialSelection';
    },

    async handleExport(selectedSubjects) {
      try {
        await exportSelectedSubjects(selectedSubjects);
        this.showExportSelector = false;
        this.currentStep = 'initialSelection';
      } catch (error) {
        console.error('Export failed:', error);
      }
    },

    processAndStoreLogIds(logs) {
      // 1. Clock Drawing PNG
      const clockDrawingPngLog = logs.find(log => 
        log.LogType === "Clock Drawing" && 
        log.LogDataType === "png"
      );
      
      if (clockDrawingPngLog) {
        console.log('Found Clock Drawing PNG log:', clockDrawingPngLog.LogId);
        localStorage.setItem('clockDrawingLogId', clockDrawingPngLog.LogId);
      } else {
        console.log('No Clock Drawing PNG log found');
        localStorage.removeItem('clockDrawingLogId');
      }
      
      // 2. Trail Making A PNG
      const trailMakingAPngLog = logs.find(log => 
        log.LogType === "Trail Making A Task" && 
        log.LogDataType === "png"
      );
      
      if (trailMakingAPngLog) {
        console.log('Found Trail Making A PNG log:', trailMakingAPngLog.LogId);
        localStorage.setItem('trailMakingALogId', trailMakingAPngLog.LogId);
      } else {
        console.log('No Trail Making A PNG log found');
        localStorage.removeItem('trailMakingALogId');
      }
      
      // 3. Trail Making B PNG
      const trailMakingBPngLog = logs.find(log => 
        log.LogType === "Trail Making B Task" && 
        log.LogDataType === "png"
      );
      
      if (trailMakingBPngLog) {
        console.log('Found Trail Making B PNG log:', trailMakingBPngLog.LogId);
        localStorage.setItem('trailMakingBLogId', trailMakingBPngLog.LogId);
      } else {
        console.log('No Trail Making B PNG log found');
        localStorage.removeItem('trailMakingBLogId');
      }
      
      // 4. Verbal Fluency - Supermarket OGG
      const verbalFluencySupermarketOggLog = logs.find(log => 
        log.LogType === "Verbal Fluency - Supermarket" && 
        log.LogDataType === "ogg"
      );
      
      if (verbalFluencySupermarketOggLog) {
        console.log('Found Verbal Fluency Supermarket OGG log:', verbalFluencySupermarketOggLog.LogId);
        localStorage.setItem('verbalFluencySupermarketLogId', verbalFluencySupermarketOggLog.LogId);
      } else {
        console.log('No Verbal Fluency Supermarket OGG log found');
        localStorage.removeItem('verbalFluencySupermarketLogId');
      }
      
      // 5. Verbal Fluency - Letter F OGG
      const verbalFluencyLetterFOggLog = logs.find(log => 
        log.LogType === "Verbal Fluency - Letter F" && 
        log.LogDataType === "ogg"
      );
      
      if (verbalFluencyLetterFOggLog) {
        console.log('Found Verbal Fluency Letter F OGG log:', verbalFluencyLetterFOggLog.LogId);
        localStorage.setItem('verbalFluencyLetterFLogId', verbalFluencyLetterFOggLog.LogId);
      } else {
        console.log('No Verbal Fluency Letter F OGG log found');
        localStorage.removeItem('verbalFluencyLetterFLogId');
      }
    },

    clearLogIds() {
      localStorage.removeItem('clockDrawingLogId');
      localStorage.removeItem('trailMakingALogId');
      localStorage.removeItem('trailMakingBLogId');
      localStorage.removeItem('verbalFluencySupermarketLogId');
      localStorage.removeItem('verbalFluencyLetterFLogId');
    }
  }
};
</script>

<style scoped>
.heading {
  font-weight: normal;
  font-size: 30px;    
  margin-bottom: 50px;
  color: #237091;
  text-align: center;
}

.content-box {
  background-color: #F4F4F4;
  padding: 40px;
  border-radius: 6px;
  border: 1px solid #a0a0a0;
  max-width: 600px;
  height: 450px;
  margin: 0 auto;
  margin-top: 50px;
  position: relative;
}

.prompt {
  text-align: center;
}

.question {
  font-size: 20px;
  margin-bottom: 20px;
}

.button-container {
  display: flex;
  flex-direction: column;
  gap: 15px;
  align-items: center;
}

.button-container > * {
  width: 200px;
  padding: 12px 0;
  text-align: center;
}

.input-container {
  margin-bottom: 20px;
  text-align: left;
}

.subject-label {
  display: block;
  font-size: 14px;
  color: #333;
  margin-bottom: 5px;
}

.subject-input {
  width: 100%;
  box-sizing: border-box;
  padding: 10px;
  font-size: 16px;
  border-radius: 8px;
  border: 2px solid #3986A8;
}

.subject-input::placeholder {
  font-size: 14px;
  color: #aaa;
}

.next-button {
  width: 100%;
  box-sizing: border-box;
  padding: 12px 0;
  display: block;
  margin: 20px auto 0;
  text-align: center;
}

.back-button, .go-back-button {
  position: absolute;
  top: 30px;
  left: 30px;
}

.error-message {
  color: #FF0000;
  font-size: 18px;
  margin-bottom: 20px;
  text-align: center;
  font-weight: bold;
}

.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.9);
  z-index: 10;
  border-radius: 6px;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.9);
  z-index: 10;
  border-radius: 6px;
}

.loading-main {
  font-weight: bold;
  margin-bottom: 20px;
  font-size: 28px;
  color: #237091;
}

.loading-sub {
  margin-bottom: 30px;
  font-size: 20px;
  color: #333;
}

.dots {
  display: flex;
  gap: 10px;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: grey;
  border: 1px solid #333;
  animation: fade 1s infinite alternate;
}

@keyframes fade {
  0% {
    background-color: grey;
  }
  100% {
    background-color: white;
  }
}

.subject-select {
  width: 100%;
  box-sizing: border-box;
  padding: 10px;
  font-size: 16px;
  border-radius: 8px;
  border: 2px solid #3986A8;
  background-color: white;
  margin-bottom: 10px;
}

.custom-input-container {
  margin-top: 15px;
}

.subject-select:focus {
  outline: none;
  border-color: #237091;
}

.go-back-button {
  position: relative;
  top: auto;
  left: auto;
  width: 150px;
}
</style>
